import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  Calendar,
  Users,
  Building2,
  Zap,
  Clock,
  Star,
  Loader2,
  CornerDownLeft,
} from 'lucide-react';
import ModalShell from './ModalShell';
import api from '../../services/authService';
import {
  ADMIN_SEARCH_PAGES,
  VENDOR_SEARCH_PAGES,
  ADMIN_SEARCH_ACTIONS,
  VENDOR_SEARCH_ACTIONS,
  filterSearchPages,
  filterSearchActions,
} from '../../utils/globalSearchIndexes';
import {
  readSearchRecents,
  pushSearchRecent,
} from '../../utils/globalSearchRecents';
import {
  readSearchPins,
  toggleSearchPin,
} from '../../utils/globalSearchPins';

function useDebouncedValue(value, delay = 280) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function extractList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data?.data)) return payload.data.data;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

/**
 * Command-palette style global search for admin / vendor shells.
 */
const GlobalSearchModal = ({ isOpen, onClose, variant = 'admin' }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [entityResults, setEntityResults] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [recents, setRecents] = useState([]);
  const [pins, setPins] = useState([]);
  const debouncedQuery = useDebouncedValue(query, 300);

  const pages = variant === 'vendor' ? VENDOR_SEARCH_PAGES : ADMIN_SEARCH_PAGES;
  const actions = variant === 'vendor' ? VENDOR_SEARCH_ACTIONS : ADMIN_SEARCH_ACTIONS;
  const qTrim = query.trim();
  const pinnedHrefs = useMemo(
    () => new Set(pins.map((item) => item.href)),
    [pins]
  );

  const pageResults = useMemo(
    () => filterSearchPages(pages, query),
    [pages, query]
  );
  const actionResults = useMemo(
    () => filterSearchActions(actions, query),
    [actions, query]
  );

  const results = useMemo(() => {
    const actionItems = actionResults.map((action) => ({
      id: `action-${action.id}`,
      type: 'action',
      label: action.label,
      meta: 'Actions',
      href: action.href,
      icon: Zap,
    }));
    const pageItems = pageResults.map((page) => ({
      id: `page-${page.id}`,
      type: 'page',
      label: page.label,
      meta: page.group,
      href: page.href,
      icon: FileText,
    }));

    if (!qTrim) {
      const pinnedItems = pins.map((item) => ({
        ...item,
        id: `pinned-${item.id || item.href}`,
        type: 'pinned',
        meta: 'Pinned',
        icon: Star,
      }));
      const recentItems = recents
        .filter((item) => !pinnedHrefs.has(item.href))
        .map((item) => ({
          ...item,
          id: `recent-${item.id || item.href}`,
          type: 'recent',
          meta: 'Recent',
          icon: Clock,
        }));
      return [...pinnedItems, ...recentItems, ...actionItems, ...pageItems];
    }

    return [...actionItems, ...pageItems, ...entityResults];
  }, [actionResults, pageResults, entityResults, recents, pins, pinnedHrefs, qTrim]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setQuery('');
    setActiveIndex(0);
    setEntityResults([]);
    setRecents(readSearchRecents(variant));
    setPins(readSearchPins(variant));
    const t = setTimeout(() => inputRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [isOpen, variant]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, entityResults.length, pins.length]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setEntityResults([]);
      setLoadingEntities(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingEntities(true);

    (async () => {
      try {
        const next = [];
        if (variant === 'admin') {
          const [bookingsRes, vendorsRes, clientsRes] = await Promise.allSettled([
            api.get('/admin/bookings', { params: { client_search: q, per_page: 5 } }),
            api.get('/admin/vendors', { params: { search: q, per_page: 5 } }),
            api.get('/admin/clients', { params: { search: q, per_page: 5 } }),
          ]);

          if (bookingsRes.status === 'fulfilled') {
            extractList(bookingsRes.value.data).slice(0, 5).forEach((booking) => {
              const ref = booking.booking_reference || booking.id;
              const who =
                booking.client?.name ||
                booking.guest_name ||
                booking.vendor?.business_name ||
                'Booking';
              next.push({
                id: `booking-${booking.id}`,
                type: 'booking',
                label: `Booking ${ref}`,
                meta: who,
                href: `/admin/bookings/${booking.id}`,
                icon: Calendar,
              });
            });
          }
          if (vendorsRes.status === 'fulfilled') {
            extractList(vendorsRes.value.data).slice(0, 5).forEach((vendor) => {
              next.push({
                id: `vendor-${vendor.id}`,
                type: 'vendor',
                label: vendor.business_name || vendor.user?.name || 'Vendor',
                meta: vendor.user?.email || 'Vendor',
                href: `/admin/vendors/${vendor.id}`,
                icon: Building2,
              });
            });
          }
          if (clientsRes.status === 'fulfilled') {
            extractList(clientsRes.value.data).slice(0, 5).forEach((client) => {
              next.push({
                id: `client-${client.id}`,
                type: 'client',
                label: client.name || client.email || 'Client',
                meta: client.email || 'Client',
                href: `/admin/clients/${client.id}`,
                icon: Users,
              });
            });
          }
        } else {
          const bookingsRes = await api.get('/vendor/bookings', {
            params: { client_search: q, per_page: 8 },
          });
          extractList(bookingsRes.data).slice(0, 8).forEach((booking) => {
            const ref = booking.booking_reference || booking.id;
            const who =
              booking.client?.name ||
              booking.guest_name ||
              booking.manual_title ||
              'Booking';
            next.push({
              id: `booking-${booking.id}`,
              type: 'booking',
              label: `Booking ${ref}`,
              meta: who,
              href: `/vendor/bookings/${booking.id}`,
              icon: Calendar,
            });
          });
        }
        if (!cancelled) setEntityResults(next);
      } catch {
        if (!cancelled) setEntityResults([]);
      } finally {
        if (!cancelled) setLoadingEntities(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, isOpen, variant]);

  const goTo = (item) => {
    if (!item?.href) return;
    pushSearchRecent(variant, item);
    onClose?.();
    navigate(item.href);
  };

  const onTogglePin = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setPins(toggleSearchPin(variant, item));
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[activeIndex]) goTo(results[activeIndex]);
    }
  };

  const placeholder =
    variant === 'vendor'
      ? 'Search pages, actions, bookings…'
      : 'Search pages, actions, vendors, clients…';

  const metaLabel = (item) => {
    if (item.type === 'pinned') return 'Pinned';
    if (item.type === 'recent') return 'Recent';
    if (item.type === 'page' || item.type === 'action') return item.meta;
    return `${item.meta} · ${item.type}`;
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="global-search-title"
      panelClassName="max-w-xl"
      className="items-start pt-[12vh] sm:pt-[15vh]"
    >
      <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
        <div className="border-b border-border px-4 pb-3 pt-4">
          <div className="flex items-center gap-2 rounded-md border border-border bg-surface-muted/60 px-3">
            <Search className="h-4 w-4 shrink-0 text-ink-tertiary" />
            <input
              ref={inputRef}
              id="global-search-title"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className="h-11 min-h-11 w-full border-0 bg-transparent text-sm text-ink placeholder:text-ink-tertiary focus:outline-none focus:ring-0"
              autoComplete="off"
            />
            {loadingEntities ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-ink-tertiary" />
            ) : (
              <kbd className="hidden shrink-0 rounded border border-border bg-surface px-1.5 py-0.5 text-[10px] font-medium text-ink-tertiary sm:inline">
                esc
              </kbd>
            )}
          </div>
        </div>

        <div className="max-h-[min(60vh,420px)] overflow-y-auto px-1 py-1">
          {results.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-ink-secondary">
              {query.trim()
                ? 'No matches. Try an action, page name, or booking reference.'
                : 'Type to search actions, pages, and records. Star items to pin them.'}
            </p>
          ) : (
            results.map((item, index) => {
              const Icon = item.icon || FileText;
              const active = index === activeIndex;
              const pinned = pinnedHrefs.has(item.href);
              return (
                <div
                  key={item.id}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 transition-colors ${
                    active ? 'bg-surface-muted' : 'hover:bg-surface-muted'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => goTo(item)}
                    className="flex min-w-0 flex-1 items-center gap-3 px-1 py-0.5 text-left"
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border text-ink ${
                        item.type === 'action'
                          ? 'border-ink bg-ink text-white'
                          : item.type === 'pinned'
                            ? 'border-border bg-surface-muted'
                            : 'border-border bg-surface'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink">
                        {item.label}
                      </span>
                      <span className="block truncate text-[11px] text-ink-tertiary">
                        {metaLabel(item)}
                      </span>
                    </span>
                    {active ? (
                      <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
                    ) : null}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => onTogglePin(e, item)}
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors ${
                      pinned
                        ? 'text-ink hover:bg-surface'
                        : 'text-ink-tertiary hover:bg-surface hover:text-ink'
                    }`}
                    aria-label={pinned ? 'Unpin' : 'Pin'}
                    title={pinned ? 'Unpin' : 'Pin'}
                  >
                    <Star
                      className="h-3.5 w-3.5"
                      fill={pinned ? 'currentColor' : 'none'}
                    />
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[11px] text-ink-tertiary">
          <span>↑↓ navigate · ↵ open · ★ pin</span>
          <span>{variant === 'vendor' ? 'Vendor search' : 'Admin search'}</span>
        </div>
      </div>
    </ModalShell>
  );
};

export default GlobalSearchModal;
